import { useState, useMemo, useEffect } from 'react';
import { Box, Flex, Button } from 'theme-ui';
import { useProductByHandle } from '@backpackjs/storefront';

import { useSelectedOptions, useProductInventoryByHandles } from '@hooks';

import { ColorOption } from './ColorOption';
import { themed } from './ColorOptions.theme';

// Enhanced ColorOption that includes badge information
function ColorOptionWithBadge({
  handle,
  selectedColor,
  setSelectedOptions,
  inventory,
  activeTab,
  onCategoryDetected,
}) {
  const { product } = useProductByHandle({ handle });

  const isLimitedEdition = useMemo(() => {
    return product?.tags?.some((tag) =>
      tag.toLowerCase().includes('badge::limited edition')
    );
  }, [product?.tags]);

  const isPersonalized = useMemo(() => {
    return product?.tags?.some((tag) =>
      tag.toLowerCase().includes('personalized-enabled')
    );
  }, [product?.tags]);

  // Report category to parent when product data is loaded
  useEffect(() => {
    if (product && onCategoryDetected) {
      onCategoryDetected(handle, {
        isLimitedEdition,
        isPersonalized,
        isCore: !isLimitedEdition,
      });
    }
  }, [product, handle, isLimitedEdition, isPersonalized, onCategoryDetected]);

  // Filter logic
  const shouldShow = useMemo(() => {
    if (activeTab === 'all') return true;
    if (activeTab === 'limited') return isLimitedEdition;
    if (activeTab === 'personalize') return isPersonalized;
    if (activeTab === 'core') return !isLimitedEdition;
    return true;
  }, [activeTab, isLimitedEdition, isPersonalized]);

  if (!shouldShow) return null;

  return (
    <ColorOption
      handle={handle}
      selectedColor={selectedColor}
      setSelectedOptions={setSelectedOptions}
      isGrouped
      inventory={inventory}
    />
  );
}

export const ColorOptions = themed(({ theme, product }) => {
  const [selectedOptions, setSelectedOptions] = useSelectedOptions();
  const [activeTab, setActiveTab] = useState('all');

  const selectedColor = selectedOptions?.color;
  const handles = product.groupOptionsMap.handle;

  const handlesWithInventory =
    useProductInventoryByHandles({
      handles,
    }) || [];

  // Track product categories as they're detected
  const [detectedCategories, setDetectedCategories] = useState(new Set());

  // Handle category detection from child components
  const handleCategoryDetected = useMemo(() => {
    const detectedHandles = new Set();
    return (handle, categories) => {
      if (detectedHandles.has(handle)) return; // Prevent duplicate counting
      detectedHandles.add(handle);

      setDetectedCategories((prev) => {
        const newCategories = new Set(prev);
        if (categories.isLimitedEdition) newCategories.add('limited');
        if (categories.isPersonalized) newCategories.add('personalize');
        if (categories.isCore) newCategories.add('core');
        return newCategories;
      });
    };
  }, []);

  // Define all possible tabs
  const allTabs = [
    { id: 'all', label: 'All' },
    { id: 'limited', label: 'Limited Edition' },
    { id: 'core', label: 'Core' },
    { id: 'personalize', label: 'Personalize' },
  ];

  // Filter tabs to only show those with products (always show 'all' if there are any products)
  const visibleTabs = allTabs.filter((tab) => {
    if (tab.id === 'all') return handlesWithInventory.length > 0;
    return detectedCategories.has(tab.id);
  });

  // Reset to 'all' tab if current tab becomes invisible
  useEffect(() => {
    if (
      visibleTabs.length > 0 &&
      !visibleTabs.some((tab) => tab.id === activeTab)
    ) {
      setActiveTab('all');
    }
  }, [visibleTabs, activeTab]);

  return product.isGrouped && handlesWithInventory?.length ? (
    <Box data-comp={ColorOptions.displayName} sx={theme.option}>
      {/* Tab Navigation */}
      <Flex sx={theme.tabs}>
        {visibleTabs.map((tab) => (
          <Button
            key={tab.id}
            variant="buttons.plain"
            onClick={() => setActiveTab(tab.id)}
            sx={{
              ...theme.tab,
              ...(activeTab === tab.id ? theme.tab.active : {}),
            }}
          >
            {tab.label}
          </Button>
        ))}
      </Flex>

      {/* Color Grid */}
      <Box as="ul" sx={theme.colorGrid}>
        {handlesWithInventory.map((handle) => (
          <ColorOptionWithBadge
            key={handle?.inventory?.handle}
            handle={handle?.inventory?.handle}
            selectedColor={selectedColor}
            setSelectedOptions={setSelectedOptions}
            inventory={handle?.inventory}
            activeTab={activeTab}
            onCategoryDetected={handleCategoryDetected}
          />
        ))}
      </Box>
    </Box>
  ) : null;
});

ColorOptions.displayName = 'ColorOptions';
